<script lang="ts">
  import { onMount } from 'svelte';
  import type { SupportGrantSummary } from '@ucc/shared';
  import {
    ApiError,
    createSupportGrant,
    fetchActiveSupportGrants,
    fetchOwnSupportGrant,
    revokeSupportGrant,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  const isClient = $derived(session.user?.role === 'client');

  // Client view state
  let grant = $state<SupportGrantSummary | null>(null);
  let durationHours = $state<number | ''>(72);
  let indefinite = $state(false);

  // Staff view state
  let activeGrants = $state<SupportGrantSummary[]>([]);
  let enteringId = $state<string | null>(null);

  let loading = $state(true);
  let error = $state<string | null>(null);
  let actionError = $state<string | null>(null);
  let actionOk = $state<string | null>(null);
  let saving = $state(false);

  function fmtWhen(iso: string | null): string {
    if (!iso) return 'Until revoked';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }

  async function load() {
    loading = true;
    error = null;
    try {
      if (isClient) {
        const res = await fetchOwnSupportGrant();
        grant = res.grant;
      } else {
        const res = await fetchActiveSupportGrants();
        activeGrants = res.grants;
      }
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load support access';
    } finally {
      loading = false;
    }
  }

  async function onCreateGrant(e: Event) {
    e.preventDefault();
    actionError = null;
    actionOk = null;
    saving = true;
    try {
      const res = await createSupportGrant({
        durationHours: indefinite ? undefined : Number(durationHours) || undefined,
      });
      grant = res.grant;
      actionOk = 'Support access allowed.';
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to allow support access';
    } finally {
      saving = false;
    }
  }

  async function onRevoke() {
    if (!window.confirm('Revoke staff support access now? Any staff currently using it will be logged out of it immediately.')) {
      return;
    }
    actionError = null;
    actionOk = null;
    saving = true;
    try {
      await revokeSupportGrant();
      grant = null;
      actionOk = 'Support access revoked.';
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to revoke support access';
    } finally {
      saving = false;
    }
  }

  async function onEnter(g: SupportGrantSummary) {
    actionError = null;
    enteringId = g.id;
    try {
      await session.enterSupport(g.tenantId);
      location.hash = '#/dashboard';
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to enter support access';
    } finally {
      enteringId = null;
    }
  }

  onMount(() => {
    void load();
  });
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>Support Access</h2>

  {#if isClient}
    <p class="sub">
      Allow Unarvu staff to sign in with their own account and act on your organization's behalf —
      to help debug your setup. No password is shared. Every action they take is logged and reversible
      by you, and you can revoke access instantly.
    </p>
  {:else}
    <p class="sub">
      Organizations that have currently allowed staff support access. Entering acts exactly as that
      organization's client until you exit — a banner stays on screen the whole time.
    </p>
  {/if}

  {#if error}
    <Card><p class="err" role="alert">{error}</p></Card>
  {:else if loading}
    <Card><p class="muted">Loading…</p></Card>
  {:else if isClient}
    <Card title="Status">
      {#if grant}
        <p class="status-line">
          <strong>Active</strong> — granted {fmtWhen(grant.createdAt)}, expires {fmtWhen(grant.expiresAt)}.
        </p>
        <Button variant="danger" disabled={saving} onclick={() => void onRevoke()}>
          {saving ? 'Revoking…' : 'Revoke support access'}
        </Button>
      {:else}
        <p class="status-line muted">Not currently allowed.</p>
        <form class="grant-form" onsubmit={(e) => void onCreateGrant(e)}>
          <label class="field">
            <span>Duration</span>
            <div class="duration-row">
              <input
                type="number"
                min="1"
                bind:value={durationHours}
                disabled={saving || indefinite}
              />
              <span class="unit">hours</span>
            </div>
          </label>
          <label class="checkbox">
            <input type="checkbox" bind:checked={indefinite} disabled={saving} />
            <span>Until I revoke it</span>
          </label>
          <Button type="submit" disabled={saving}>
            {saving ? 'Allowing…' : 'Allow support access'}
          </Button>
        </form>
      {/if}
      {#if actionError}
        <p class="err" role="alert">{actionError}</p>
      {/if}
      {#if actionOk}
        <p class="ok" role="status">{actionOk}</p>
      {/if}
    </Card>
  {:else}
    <Card title="Active grants" padded={false}>
      {#if activeGrants.length === 0}
        <p class="banner muted">No organization currently has support access allowed.</p>
      {:else}
        <ul class="list">
          {#each activeGrants as g (g.id)}
            <li>
              <div>
                <span class="name">{g.tenantName}</span>
                <span class="meta">Expires {fmtWhen(g.expiresAt)}</span>
              </div>
              <Button disabled={enteringId === g.id} onclick={() => void onEnter(g)}>
                {enteringId === g.id ? 'Entering…' : 'Enter'}
              </Button>
            </li>
          {/each}
        </ul>
      {/if}
      {#if actionError}
        <p class="err banner-pad" role="alert">{actionError}</p>
      {/if}
    </Card>
  {/if}
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-width: 44rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.5;
  }
  .muted {
    color: var(--text-muted);
  }
  .err {
    color: #f87171;
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .ok {
    color: var(--success, #22c55e);
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .banner.muted {
    padding: 1rem;
    margin: 0;
  }
  .banner-pad {
    padding: 0 1rem 0.75rem;
  }
  .status-line {
    margin: 0 0 0.85rem;
  }
  .grant-form {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    align-items: flex-start;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-size: 0.9rem;
  }
  .field span {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
  .duration-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .duration-row input {
    font: inherit;
    padding: 0.45rem 0.6rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--surface-2, #fff);
    color: inherit;
    width: 6rem;
  }
  .unit {
    color: var(--text-muted);
    font-size: 0.85rem;
  }
  .checkbox {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.65rem 1rem;
    border-bottom: 1px solid var(--border);
  }
  .name {
    font-weight: 600;
    display: block;
  }
  .meta {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
</style>
